import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { AllergyIntolerance } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '../../utils/date';
import { type PromotionStatus, RecordListItem } from './RecordListItem';

export type AllergiesListProps = {
  allergies: WithId<AllergyIntolerance>[];
  onSelectAllergy?: (id: string) => void;
  selectedAllergyId?: string;
  onPromotionClick?: (id: string) => void;
  getPromotionStatus?: (id: string) => PromotionStatus;
};

const getAllergyDescription = (allergy: AllergyIntolerance, t: TFunction<'records'>): string => {
  const reactions = allergy.reaction
    ?.map((reaction) => formatCodeableConcept(reaction.manifestation?.at(0)))
    .join(', ');

  return [
    reactions && `${t('reactions')}: ${reactions}`,
    allergy.recordedDate && t('recordedOnDate', { date: format(parseISO(allergy.recordedDate), DATE_FORMATS.DATE) }),
    allergy.onsetDateTime && t('onsetDateValue', { date: format(parseISO(allergy.onsetDateTime), DATE_FORMATS.DATE) }),
  ]
    .filter(Boolean)
    .join(' · ');
};

export const AllergiesList = ({
  allergies,
  onSelectAllergy,
  selectedAllergyId,
  onPromotionClick,
  getPromotionStatus,
}: AllergiesListProps): React.ReactElement => {
  const { t } = useTranslation('records');

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="space-y-2">
        {allergies.length > 0 ? (
          allergies.map((allergy) => (
            <RecordListItem
              key={allergy.id}
              title={allergy.code ? formatCodeableConcept(allergy.code) : t('unknownAllergy')}
              description={getAllergyDescription(allergy, t)}
              onClick={onSelectAllergy ? (): void => onSelectAllergy(allergy.id) : undefined}
              selected={selectedAllergyId === allergy.id}
              onPromotionClick={onPromotionClick ? (): void => onPromotionClick(allergy.id) : undefined}
              promotionStatus={getPromotionStatus?.(allergy.id)}
            />
          ))
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('noAllergiesFound')}</p>
        )}
      </div>
    </div>
  );
};
